'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarkets } from '@morfi/backend-sdk-v2/client';
import { MARKETS_LIST_OPEN_STATUS } from '@/lib/market/listing-status';
import { HERO_MARKET_FIELDS, type HeroMarket } from './hero-market';
export { HERO_MARKET_FIELDS };

/**
 * Size of the candidate pool handed downstream. This is NOT the slide count —
 * the carousel renders far fewer. The pool is deliberately wide so the
 * `outcomes.length >= 2` guard has markets to work with and never starves the
 * carousel. Bounded well under the backend's 50-row default page so it stays a
 * cheap leading slice.
 */
const MAX_HERO_CANDIDATES = 24;
export interface UseHeroCandidatesReturn {
  /** Top markets by 24h trading volume for the hero carousel (whole rows). */
  candidates: ReadonlyArray<HeroMarket>;
  isPending: boolean;
  isError: boolean;
}

/**
 * Dedicated scoped query for the featured-market hero carousel, ranked by 24h
 * trading volume — the busiest markets lead the carousel.
 *
 * `volume24h` is the independently supported activity signal for this hero;
 * chronology remains the honest default for ordinary market lists.
 *
 * The hero is a single-slide presentation surface, not a list — it needs
 * whole market rows at the parent to filter thin markets and pick a slide.
 *
 * Explicit rows access subscribes this hook's owner to live entity changes.
 * Keep that owner inside the featured section, away from the feed controller.
 */
export function useHeroCandidates() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      sortBy: "volume24h",
      sortOrder: "desc",
      status: MARKETS_LIST_OPEN_STATUS
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const query = useGetMarkets(t0);
  let t1;
  if ($[1] !== query.rows) {
    t1 = query.rows.filter(_temp).map(_temp2).slice(0, MAX_HERO_CANDIDATES);
    $[1] = query.rows;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const candidates = t1;
  let t2;
  if ($[3] !== candidates || $[4] !== query.isError || $[5] !== query.isPending) {
    t2 = {
      candidates,
      isPending: query.isPending,
      isError: query.isError
    };
    $[3] = candidates;
    $[4] = query.isError;
    $[5] = query.isPending;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp2(r_0) {
  return r_0 as HeroMarket;
}
function _temp(r) {
  return Array.isArray(r.outcomes);
}